import {
  Popover,
  PopoverButton,
  PopoverContent,
  useToastProvider,
} from "@deere/fuel-react";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import { Box, ListItemButton, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import i18n from "i18next";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { PreferenceContext } from "../../../../../context/PreferencesContext/PreferencesContext";
import Language from "../../../../../i18n/Language";
import { Preference } from "../../../../../pages/preferences/types";

const walkmeLanguageMapping = {
  en_US: "en",
  es_MX: "es",
  pt_BR: "pt",
  zh_CN: "cmn",
  fr_CA: "fr",
  de_DE: "de",
  it_IT: "it",
  pl_PL: "pl",
};

const getWalkmeMapping = (lang: keyof typeof walkmeLanguageMapping) =>
  walkmeLanguageMapping[lang] || lang;

const LanguagePref = () => {
  const { enqueueToast } = useToastProvider();
  const { masterPreferences, userPreferences, saveUserPreferences } =
    useContext(PreferenceContext);
  const getLangFromLangPref = () => {
    const lang = userPreferences.find(({ key }) => key === "language")?.value;
    return lang || Language.ENGLISH;
  };
  const userSelectedLang = getLangFromLangPref();
  const [effectiveLang, setEffectiveLang] = useState(userSelectedLang);

  const theme = useTheme();
  const langMasterPref = masterPreferences.find(
    ({ key }) => key === "language"
  );
  const options = langMasterPref
    ? langMasterPref.options
    : [effectiveLang].sort();
  const { t } = useTranslation("common");
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Popover
      showClose={false}
      isOpen={isOpen}
      onClose={(): void => setIsOpen(false)}
    >
      <PopoverButton
        onClick={(): void => setIsOpen(true)}
        data-testid="lang-button"
        aria-label="language"
        variant="text"
        sx={{
          ":hover": {
            background: theme.tokens.FuelColorGray50,
          },
          ":active": {
            background: theme.tokens.FuelColorBackgroundActionSecondaryPressed,
          },
        }}
      >
        <Box sx={{ position: "relative", lineHeight: "0", padding: "6px" }}>
          <LanguageOutlinedIcon
            data-testid="lang-icon"
            sx={{
              color: theme.tokens.FuelColorTextIcon,
            }}
          />
        </Box>

        <Typography
          data-testid="lang-value"
          sx={{
            color: theme.tokens.FuelColorTextIcon,
            fontWeight: 600,
          }}
        >
          {t(`header.languagePreference.language.${effectiveLang}`)}
        </Typography>
      </PopoverButton>
      <PopoverContent>
        <Box minWidth="200px">
          {options.map((lang) => (
            <ListItemButton
              data-testid={`lang-item-${lang}`}
              key={lang}
              sx={{
                pl: 5,
                height: "40px",
              }}
              selected={lang === effectiveLang}
              onClick={() => {
                const preference: Preference = {
                  key: "language",
                  value: lang,
                  userOverridable: true,
                };
                setEffectiveLang(lang);
                i18n.changeLanguage(lang);

                const walkmeChangeLanguage = window?.WalkMeAPI?.changeLanguage;

                if (walkmeChangeLanguage) {
                  const walkmeLanguage = getWalkmeMapping(
                    lang as keyof typeof walkmeLanguageMapping
                  );
                  walkmeChangeLanguage(walkmeLanguage);
                }

                saveUserPreferences([preference])
                  .then(() => {
                    enqueueToast({
                      variant: "success",
                      message: t(
                        "header.languagePreference.saveActionCompleted"
                      ),

                      isCloseable: true,
                    });
                    setIsOpen(false);
                  })
                  .catch(() => {
                    i18n.changeLanguage(lang);
                    enqueueToast({
                      variant: "error",
                      message: t("header.languagePreference.saveActionFailed"),
                      isCloseable: true,
                    });
                    setIsOpen(false);
                  });
              }}
            >
              {t(`header.languagePreference.language.${lang}`)}
            </ListItemButton>
          ))}
        </Box>
      </PopoverContent>
    </Popover>
  );
};

export default LanguagePref;
